import ApiService from '@/services/api';
import { EndpointKeys } from '@/config/api';
import {
  TaskStatusResponse,
  TaskHistory,
  TaskHistoryFilters,
} from '@/types/taskStatus';

export class TaskStatusService {
  /**
   * Get summary of all tracked tasks with their latest execution status
   */
  static async getTaskStatusSummary(): Promise<TaskStatusResponse> {
    return ApiService.request<TaskStatusResponse>(
      EndpointKeys.TASK_STATUS_SUMMARY,
      {
        method: 'GET',
      }
    );
  }

  /**
   * Get detailed execution history for a specific task
   */
  static async getTaskHistory(
    taskKey: string,
    options?: TaskHistoryFilters
  ): Promise<TaskHistory> {
    // Build query parameters from options
    const queryParams: Record<string, string | number> = {};
    
    if (options?.limit !== undefined) {
      queryParams.limit = options.limit;
    }
    
    if (options?.offset !== undefined) {
      queryParams.offset = options.offset;
    }
    
    if (options?.startDate) {
      queryParams.start_date = options.startDate;
    }
    
    if (options?.endDate) {
      queryParams.end_date = options.endDate;
    }

    // Replace {task_key} placeholder in the endpoint URL
    const endpoint = EndpointKeys.TASK_HISTORY;
    
    return ApiService.request<TaskHistory>(
      endpoint,
      {
        method: 'GET',
        urlParams: { task_key: taskKey },
        queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
      }
    );
  }

  /**
   * Get task history with default pagination settings
   */
  static async getTaskHistoryPage(
    taskKey: string,
    page: number = 1,
    limit: number = 50
  ): Promise<TaskHistory> {
    const offset = (page - 1) * limit;
    
    return this.getTaskHistory(taskKey, {
      limit,
      offset,
    });
  }

  /**
   * Get task history filtered by date range
   */
  static async getTaskHistoryByDateRange(
    taskKey: string,
    startDate: string,
    endDate: string,
    limit: number = 100
  ): Promise<TaskHistory> {
    return this.getTaskHistory(taskKey, {
      startDate,
      endDate,
      limit,
      offset: 0,
    });
  }
}

export default TaskStatusService; 